import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import { Currency, PaymentMethod, PlanPriceConfiguration } from "@/types";
import { PostgrestError } from "@supabase/supabase-js";

type FeeType = "percentage" | "fixed";

interface BasePaymentMethod {
  id: number;
  name: string;
  description: string;
  platform_fee: number;
  fee_type: FeeType;
  supported_currencies: string[];
  is_default: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface BasePlanPriceConfig {
  id: number;
  plan_id: number;
  payment_method_id: number;
  currency_code: string;
  price: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface PaymentMethodRow {
  id: number;
  name: string;
  description: string | null;
  platform_fee: number;
  fee_type: FeeType;
  supported_currencies: string[];
  is_default: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface PlanPriceConfigRow {
  id: number;
  plan_id: number;
  payment_method_id: number;
  currency_code: string;
  price: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

type PaymentMethodInsert = Omit<
  BasePaymentMethod,
  "id" | "created_at" | "updated_at"
>;
type PlanPriceConfigInsert = Omit<
  BasePlanPriceConfig,
  "id" | "created_at" | "updated_at"
>;

interface QueryResult<T> {
  data: T | null;
  error: PostgrestError | null;
}

// Define Supabase table types
type Tables = {
  payment_methods: {
    Row: {
      id: number;
      name: string;
      description: string | null;
      platform_fee: number;
      fee_type: "percentage" | "fixed";
      supported_currencies: string[];
      is_default: boolean;
      is_active: boolean;
      created_at: string;
      updated_at: string;
    };
    Insert: Omit<
      Tables["payment_methods"]["Row"],
      "id" | "created_at" | "updated_at"
    >;
    Update: Partial<Tables["payment_methods"]["Insert"]>;
  };
  plan_price_configurations: {
    Row: {
      id: number;
      plan_id: number;
      payment_method_id: number;
      currency_code: string;
      price: number;
      is_active: boolean;
      created_at: string;
      updated_at: string;
    };
    Insert: Omit<
      Tables["plan_price_configurations"]["Row"],
      "id" | "created_at" | "updated_at"
    >;
    Update: Partial<Tables["plan_price_configurations"]["Insert"]>;
  };
  currencies: {
    Row: {
      code: string;
      name: string;
      symbol: string;
      exchange_rate: number;
      is_active: boolean;
      last_updated: string;
    };
    Insert: Omit<Tables["currencies"]["Row"], "last_updated">;
    Update: Partial<Tables["currencies"]["Insert"]>;
  };
};

// ------- Currency Management -------

export const fetchCurrencies = async (): Promise<Currency[]> => {
  try {
    const { data, error } = await supabase
      .from("currencies")
      .select("*")
      .order("code", { ascending: true })
      .eq("is_active", true);

    if (error) throw error;

    return (data || []).map((row) => ({
      code: row.code,
      symbol: row.symbol,
      name: row.name,
      exchangeRate: row.exchange_rate,
      isActive: row.is_active,
      lastUpdated: row.last_updated,
    }));
  } catch (error) {
    console.error("Error fetching currencies:", error);

    // Fallback to hardcoded data if database query fails
    return [
      {
        code: "USD",
        symbol: "$",
        name: "US Dollar",
        exchangeRate: 1,
        isActive: true,
      },
      {
        code: "INR",
        symbol: "₹",
        name: "Indian Rupee",
        exchangeRate: 83.12,
        isActive: true,
      },
      {
        code: "MMK",
        symbol: "K",
        name: "Myanmar Kyat",
        exchangeRate: 4400,
        isActive: true,
      },
    ];
  }
};

// ------- Payment Method Management -------

// Transform from Supabase format to our app format
const transformPaymentMethod = (row: PaymentMethodRow): PaymentMethod => ({
  id: row.id,
  name: row.name,
  description: row.description,
  platformFee: row.platform_fee,
  feeType: row.fee_type,
  supportedCurrencies: row.supported_currencies,
  isDefault: row.is_default,
  isActive: row.is_active,
  createdAt: row.created_at,
  updatedAt: row.updated_at,
});

export const fetchPaymentMethods = async (): Promise<PaymentMethod[]> => {
  try {
    const { data, error } = await supabase
      .from("payment_methods")
      .select("*")
      .order("created_at", { ascending: false });

    if (error) throw error;
    return (data || []).map(transformPaymentMethod);
  } catch (error) {
    console.error("Error fetching payment methods:", error);
    throw error;
  }
};

export const fetchPaymentMethod = async (
  id: number
): Promise<PaymentMethod | null> => {
  try {
    const { data, error } = await supabase
      .from("payment_methods")
      .select("*")
      .eq("id", id)
      .single();

    if (error) {
      if (error.code === "PGRST116") return null;
      throw error;
    }

    return data ? transformPaymentMethod(data) : null;
  } catch (error) {
    console.error("Error fetching payment method:", error);
    throw error;
  }
};

export const createPaymentMethod = async (
  paymentMethod: Omit<PaymentMethod, "id" | "createdAt" | "updatedAt">
): Promise<PaymentMethod> => {
  try {
    const { data, error } = await supabase
      .from("payment_methods")
      .insert({
        name: paymentMethod.name,
        description: paymentMethod.description,
        platform_fee: paymentMethod.platformFee,
        fee_type: paymentMethod.feeType,
        supported_currencies: paymentMethod.supportedCurrencies,
        is_default: paymentMethod.isDefault,
        is_active: paymentMethod.isActive,
      })
      .select()
      .single();

    if (error) throw error;
    if (!data) throw new Error("No data returned after insert");
    return transformPaymentMethod(data);
  } catch (error) {
    console.error("Error creating payment method:", error);
    throw error;
  }
};

export const updatePaymentMethod = async (
  id: number,
  paymentMethod: Partial<Omit<PaymentMethod, "id" | "createdAt" | "updatedAt">>
): Promise<PaymentMethod> => {
  try {
    const { data, error } = await supabase
      .from("payment_methods")
      .update({
        name: paymentMethod.name,
        description: paymentMethod.description,
        platform_fee: paymentMethod.platformFee,
        fee_type: paymentMethod.feeType,
        supported_currencies: paymentMethod.supportedCurrencies,
        is_default: paymentMethod.isDefault,
        is_active: paymentMethod.isActive,
      })
      .eq("id", id)
      .select()
      .single();

    if (error) throw error;
    if (!data) throw new Error("No data returned after update");
    return transformPaymentMethod(data);
  } catch (error) {
    console.error("Error updating payment method:", error);
    throw error;
  }
};

export const deletePaymentMethod = async (id: number): Promise<void> => {
  try {
    const { error } = await supabase
      .from("payment_methods")
      .delete()
      .eq("id", id);

    if (error) throw error;
  } catch (error) {
    console.error("Error deleting payment method:", error);
    throw error;
  }
};

// ------- Plan Price Configuration Management -------

// Transform from Supabase format to our app format
const transformPlanPriceConfig = (
  row: PlanPriceConfigRow
): PlanPriceConfiguration => ({
  id: row.id,
  planId: row.plan_id,
  paymentMethodId: row.payment_method_id,
  currencyCode: row.currency_code,
  price: row.price,
  isActive: row.is_active,
  createdAt: row.created_at,
  updatedAt: row.updated_at,
});

export const fetchPlanPriceConfigurations = async (
  planId: number
): Promise<PlanPriceConfiguration[]> => {
  try {
    const { data, error } = await supabase
      .from("plan_price_configurations")
      .select("*")
      .eq("plan_id", planId);

    if (error) {
      console.error("Error fetching plan price configurations:", error);
      throw error;
    }

    return (data || []).map(transformPlanPriceConfig);
  } catch (error) {
    console.error("Error in fetchPlanPriceConfigurations:", error);
    throw error;
  }
};

export const createPlanPriceConfiguration = async (
  config: Omit<PlanPriceConfiguration, "id">
): Promise<PlanPriceConfiguration> => {
  try {
    const { data, error } = await supabase
      .from("plan_price_configurations")
      .insert({
        plan_id: config.planId,
        payment_method_id: config.paymentMethodId,
        currency_code: config.currencyCode,
        price: config.price,
        is_active: config.isActive,
      })
      .select()
      .single();

    if (error) {
      console.error("Error creating plan price configuration:", error);
      throw error;
    }

    if (!data) throw new Error("No data returned after insert");
    return transformPlanPriceConfig(data);
  } catch (error) {
    console.error("Error in createPlanPriceConfiguration:", error);
    throw error;
  }
};

export const updatePlanPriceConfiguration = async (
  id: number,
  config: Partial<Omit<PlanPriceConfiguration, "id">>
): Promise<PlanPriceConfiguration> => {
  try {
    const { data, error } = await supabase
      .from("plan_price_configurations")
      .update({
        payment_method_id: config.paymentMethodId,
        currency_code: config.currencyCode,
        price: config.price,
        is_active: config.isActive,
      })
      .eq("id", id)
      .select()
      .single();

    if (error) {
      console.error("Error updating plan price configuration:", error);
      throw error;
    }

    if (!data) throw new Error("No data returned after update");
    return transformPlanPriceConfig(data);
  } catch (error) {
    console.error("Error in updatePlanPriceConfiguration:", error);
    throw error;
  }
};

export const deletePlanPriceConfiguration = async (
  id: number
): Promise<void> => {
  try {
    const { error } = await supabase
      .from("plan_price_configurations")
      .delete()
      .eq("id", id);

    if (error) {
      console.error("Error deleting plan price configuration:", error);
      throw error;
    }
  } catch (error) {
    console.error("Error in deletePlanPriceConfiguration:", error);
    throw error;
  }
};

// ------- Helper Functions -------

export const calculatePriceWithFee = (
  basePrice: number,
  paymentMethod: PaymentMethod
): number => {
  if (paymentMethod.feeType === "percentage") {
    const fee = basePrice * (paymentMethod.platformFee / 100);
    return parseFloat((basePrice + fee).toFixed(2));
  } else {
    // Fixed fee
    return parseFloat((basePrice + paymentMethod.platformFee).toFixed(2));
  }
};

export const getActivePaymentMethodsForCurrency = (
  paymentMethods: PaymentMethod[],
  currencyCode: string
): PaymentMethod[] => {
  return paymentMethods.filter(
    (method) =>
      method.isActive && method.supportedCurrencies.includes(currencyCode)
  );
};

export const getDefaultPaymentMethod = (
  paymentMethods: PaymentMethod[],
  currencyCode: string
): PaymentMethod | undefined => {
  const availableMethods = getActivePaymentMethodsForCurrency(
    paymentMethods,
    currencyCode
  );
  return availableMethods.find((method) => method.isDefault);
};

// Format price with currency symbol
export const formatPriceWithCurrency = (
  price: number,
  currency: Currency
): string => {
  // Different currencies have different formatting standards
  switch (currency.code) {
    case "USD":
      return `${currency.symbol}${price.toFixed(2)}`;
    case "INR":
      return `${currency.symbol}${price.toFixed(0)}`;
    case "MMK":
      return `${price.toFixed(0)} ${currency.symbol}`;
    default:
      return `${currency.symbol}${price.toFixed(2)}`;
  }
};
